import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import BlackList from "../../components/BlackList/BlackList";
import LimitButton from "../../components/Commons/LimitSelectBox";
import Pagination from '../../components/Commons/TablePagination';
import useDebounce from "../../lib/useDebounce";
import { RootState } from "../../stores";
import { listBlackAction, setCurrentPageNumberAction, setDetailField, setFilterItemAction, setInitFilterItemAction, setPaginationItemAction } from "../../stores/blackListList";
import { BlackListContainerProps, blackListState } from "../../types/blackList";
import BlackCreateContainer from "./BlackCreateContainer";
import BlackDeleteContainer from "./BlackDeleteContainer";
import BlackDetailContainer from "./BlackDetailContainer";
import BlackUpdateContainer from "./BlackUpdateContainer";

/* styled */
const FormCard = styled.article`
  width: 100%;
  position: relative;
`;

const BlackListContainer = ({
  isOpen,
  blackListView,
  breadcrumbItems,
  handleViewChange
}:BlackListContainerProps) => {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const { blackListTotal, blackListItems, blackListError, currentPageNumber, paginationItem, filterItem, userRole } = useSelector(({ blackListList, header }:RootState) => ({
    blackListTotal: blackListList.blackListTotal,
    blackListItems: blackListList.blackListItems,
    blackListError: blackListList.blackListError,
    currentPageNumber: blackListList.currentPageNumber,
    paginationItem: blackListList.paginationItem,
    filterItem: blackListList.filterItem,
    userRole: header.userRole,
  }));
  const [ isDebounce, setIsDebounce ] = useState<boolean>(false); //디바운싱 사용 구분을 위해 추가
  const [ isOpenBlackListCreateModal, setIsOpenBlackListCreateModal ] = useState<boolean>(false);
  const [ isOpenBlackListUpdateModal, setIsOpenBlackListUpdateModal ] = useState<boolean>(false);
  const [ isOpenBlackListDeleteModal, setIsOpenBlackListDeleteModal ] = useState<boolean>(false);
  const [ selectedBlackListId, setSelectedBlackListId ] = useState<string>('');
  const [ selectedBlackListName, setSelectedBlackListName ] = useState<string>('');

  const handleListBlackList = useCallback(() => {
    dispatch(listBlackAction({...filterItem, ...paginationItem}));
  }, [dispatch, filterItem, paginationItem]);

  //필터 핸들링
  const handleFilter =  useCallback((targetItem:any, isDebounce:boolean) => {
    setIsDebounce(isDebounce); //디바운싱 처리를 위해 하위 Component에서 구분을 받아옴
    dispatch(setFilterItemAction({...filterItem, ...targetItem}));
  }, [dispatch, filterItem]);

  //필터 초기화 버튼
  const handleinitFilter = useCallback(() => {
    dispatch(setInitFilterItemAction());
  },[dispatch]);

  //상세화면 이동용
  const handleBlackListDetail = useCallback((id:string) => {
    handleViewChange('blackList', 'detail');
    dispatch(setDetailField(id));
  },[handleViewChange, dispatch]);

  //모달 플래그
  const handleBlackListCreateModal = () => {
    setIsOpenBlackListCreateModal(true);
  };

  const handleBlackListUpdateModal = useCallback((id:string) => {
    setSelectedBlackListId(id);
    setIsOpenBlackListUpdateModal(true);
  },[]);

  const handleBlackListDeleteModal = useCallback((id:string, room_no:string) => {
    setSelectedBlackListId(id);
    setSelectedBlackListName(room_no);
    setIsOpenBlackListDeleteModal(true);
  },[]);

  //현재 페이지 전역상태 등록
  const handleCurrentPageNumber = useCallback((currentPageNumber:number) => {
    dispatch(setCurrentPageNumberAction({currentPageNumber}));
  }, [dispatch]);

  //페이지 전역상태 등록
  const handlePaginationItem = useCallback((paginationItem:blackListState['paginationItem']) => {
    dispatch(setPaginationItemAction({paginationItem}));
  }, [dispatch]);

  //페이지네이션 이동
  const changePagination = useCallback((pageNumber:number) => {
    handleCurrentPageNumber(pageNumber);
    handlePaginationItem({
      ...paginationItem,
      offset: (pageNumber - 1) * paginationItem.limit,
    });
  },[handleCurrentPageNumber, handlePaginationItem, paginationItem]);

  //목록 리미트
  const changeLimit = useCallback((limit:number) => {
    handleCurrentPageNumber(1);
    handlePaginationItem({
      offset: 0,
      limit,
    });
  },[handleCurrentPageNumber, handlePaginationItem]);

  //디바운싱 훅으로 이동
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebounce = useCallback(
    useDebounce((handleListBlackListing) => handleListBlackListing(), 300) //0.3초 동안 미입력 시 함수 실행
  ,[]);

  useEffect(() => {
    try {
      if (isOpen) !isDebounce? handleListBlackList() : handleDebounce(handleListBlackList); //디바운싱 처리
    } catch (error) {
      throw error;
    }
  }, [isOpen, handleListBlackList, isDebounce, handleDebounce]);
  
  useEffect(() => {
    if(blackListError){
      if(blackListError.response.data?.code === 401 || blackListError.response.data?.code === 419){
          localStorage.clear();
          navigation('/login');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[blackListError]);

  return (
    <>
    {blackListView === 'list' && <>
        <FormCard hidden={blackListView !== 'list'}>
          <BlackList 
            blackListItems={blackListItems}
            filterItem={filterItem}
            userRole={userRole}
            blackListTotal={blackListTotal}
            handleFilter={handleFilter}
            handleinitFilter={handleinitFilter}
            handleBlackListDetail={handleBlackListDetail}
            handleBlackListCreateModal={handleBlackListCreateModal}
            handleBlackListUpdateModal={handleBlackListUpdateModal}
            handleBlackListDeleteModal={handleBlackListDeleteModal}
          />
          <LimitButton
            currentLimit={paginationItem.limit}
            changeLimit={changeLimit}
          />
          <Pagination
            total={blackListTotal}
            index={currentPageNumber}
            limit={paginationItem.limit}
            indexChange={changePagination}
          />
        </FormCard>
        <BlackCreateContainer
          breadcrumbItems={breadcrumbItems}
          isOpen={isOpenBlackListCreateModal}
          toggle={() => setIsOpenBlackListCreateModal(!isOpenBlackListCreateModal)}
          reload={() => handleListBlackList()}
        />
        <BlackUpdateContainer
          isOpen={isOpenBlackListUpdateModal}
          toggle={() => setIsOpenBlackListUpdateModal(!isOpenBlackListUpdateModal)}
          reload={() => handleListBlackList()}
          id={selectedBlackListId}
        />
        <BlackDeleteContainer
          isOpen={isOpenBlackListDeleteModal}
          toggle={() => setIsOpenBlackListDeleteModal(!isOpenBlackListDeleteModal)}
          reload={() => handleListBlackList()}
          id={selectedBlackListId}
          blackListName={selectedBlackListName}
        />
      </>
    }
    {blackListView === 'detail' &&
      <BlackDetailContainer
        isOpen={blackListView === 'detail'}
        listBlack={handleListBlackList}
        handleViewChange={handleViewChange}
      />
    }
    </>
  )
};

export default BlackListContainer;